import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import { useTableContext } from "../table.context"
import { getColumnSizeStyle, isInternalColumnId } from "../table.utils"
import TableSummaryCell from "./TableSummaryCell"

export type TableSummaryRowProps = {
  /** The server holds rows the table hasn't loaded yet, so the answers are provisional. */
  isPartial?: boolean
}

/**
 * The footer that keeps one summary cell under every visible column, so a
 * calculation sits right below the values it describes. Sticky to the bottom
 * of the scroll area, like a spreadsheet's totals line; Table.Content mounts
 * it while the toolbar toggle is on and there are rows. Internal columns
 * (selection, actions, spacer) get an empty cell so the line runs the full
 * width.
 *
 * While the rest of a server-paginated list is on its way, the row's top
 * border turns into a progress line and the answers show muted, so a
 * provisional total is never mistaken for the final one.
 */
function TableSummaryRow<TData = unknown>(props: TableSummaryRowProps) {
  const { isPartial = false } = props
  const { table, hasPersistence, countMeta } = useTableContext<TData>()
  const columnSizing = table.getState().columnSizing
  const columns = table.getVisibleLeafColumns()

  const loaded = table.getCoreRowModel().rows.length
  const total = countMeta.total ?? loaded
  const progress = total > 0 ? Math.min(loaded / total, 1) : 0

  return (
    <tfoot
      className={cn("ui-table-summary", {
        "ui-table-summary-is-partial": isPartial,
      })}
    >
      {isPartial && (
        <tr className="ui-table-summary-progress">
          <td
            colSpan={columns.length}
            className="ui-table-summary-progress-cell"
            role="status"
          >
            <span
              className="ui-table-summary-progress-bar"
              style={{ width: `${progress * 100}%` }}
            />
            <span className="sr-only">
              {`Loading ${formatNumber(loaded)} of ${formatNumber(total)} ${countMeta.label}`}
            </span>
          </td>
        </tr>
      )}
      <tr className="ui-table-summary-row">
        {columns.map((column) => {
          const style = getColumnSizeStyle({
            column,
            columnSizing,
            hasPersistence,
          })
          if (isInternalColumnId(column.id)) {
            return (
              <td
                key={column.id}
                className="ui-table-summary-cell"
                style={style}
              />
            )
          }
          return (
            <TableSummaryCell key={column.id} column={column} style={style} />
          )
        })}
      </tr>
    </tfoot>
  )
}

export default TableSummaryRow
